import { useEffect, useRef, useState } from "react";

import { isSupabaseConfigured } from "./supabase";
import {
  createLiveRefresh,
  formatUpdatedLabel,
  stampAdvanced,
  updatedAtText,
  updatedAtTitle,
  type UpdatedAtStamp,
} from "./tabFreshness.mjs";
import {
  fetchTabUpdatedAt,
  publishTabUpdatedAt,
  readTabUpdatedAt,
  type TabUpdatedAt,
} from "./tabFreshnessData";
import { useTenantId } from "./tenant";

/**
 * AJ 2026-09-30: "Delta is expecting updates every hour for this report." A
 * report tab left open refreshes itself when a new sync lands. This is the
 * one implementation; every report port and the Executive page call it.
 *
 *   const updated = useLiveReport(entityForReport("ro_efficiency"), refreshRoEfficiency);
 *   ... <UpdatedAtChip updated={updated} />
 *
 * THE POLL. While the tab is visible, one sync_log read every 120 s
 * (readTabUpdatedAt: tenant-scoped, status success, order by completed_at
 * desc, limit 1). Hidden: no reads. Back to visible, or window focus: one read
 * at once and the interval restarts. The loop is createLiveRefresh
 * (tabFreshness.mjs), tested with mocked timers.
 *
 * THE SWAP. When the newest completed_at passes the stamp the page's data was
 * loaded under, `refetch(tenantId)` re-reads the report's cache in the
 * background (LiveCache.refresh: all answers land, or none are swapped).
 * Only then does the chip move to the new time (reading "Updated just now"
 * for a few seconds) and the sidebar badge follow (publishTabUpdatedAt). A
 * failed refetch keeps the old data and the old time, with no error card;
 * the next poll tries again.
 *
 * `refetch` must be a stable module-level function: the stamp a report's
 * data was loaded under is remembered per refetch function, so leaving the
 * tab and coming back does not mistake old cached data for new.
 *
 * COMING BACK TO A TAB (AJ 2026-10-01, speed). A report keeps what it read for
 * the page load, so a tab the reader left and returns to draws at once with
 * no read. The data can only have changed if a sync landed meanwhile, so a
 * return checks exactly that: one sync_log read on mount, unless this tab's
 * record was read within the last REVISIT_POLL_AFTER_MS. A newer sync swaps
 * in behind the figures on screen, as a poll does; no newer sync, nothing
 * else is read.
 */

/** A return to a tab re-checks the sync record unless it was read this recently. */
export const REVISIT_POLL_AFTER_MS = 30_000;

/** `${tenant}|${entity}` -> when its sync record was last read (ms). */
const lastChecked = new Map<string, number>();

function noteChecked(key: string): void {
  lastChecked.set(key, Date.now());
}

export interface LiveTabUpdatedAt extends TabUpdatedAt {
  /** True for a few seconds after new data was swapped in. */
  justUpdated: boolean;
}

/**
 * The chip's words: "Updated @ " + "4:52 PM", or for a few seconds after a
 * live swap, "Updated " + "just now". One wording for every chip.
 */
export function chipWords(updated: LiveTabUpdatedAt): { prefix: string; time: string } {
  return updated.justUpdated
    ? { prefix: "Updated ", time: "just now" }
    : { prefix: "Updated @ ", time: updated.label };
}

export type LiveRefetch = (tenantId: string) => Promise<unknown>;

/** refetch fn -> `${tenant}|${entity}` -> the stamp its cached data is from. */
const loadedUnder = new WeakMap<LiveRefetch, Map<string, UpdatedAtStamp | null>>();

function remembered(fn: LiveRefetch): Map<string, UpdatedAtStamp | null> {
  let m = loadedUnder.get(fn);
  if (!m) {
    m = new Map();
    loadedUnder.set(fn, m);
  }
  return m;
}

/** Test / tenant-switch seam. */
export function __resetLiveReportMemory(fn: LiveRefetch): void {
  loadedUnder.delete(fn);
  lastChecked.clear();
}

function isVisible(): boolean {
  return typeof document === "undefined" || document.visibilityState === "visible";
}

export function useLiveReport(
  entityKey: string | null | undefined,
  refetch: LiveRefetch,
): LiveTabUpdatedAt | null {
  const tenantId = useTenantId();
  const key = tenantId && entityKey && isSupabaseConfigured() ? `${tenantId}|${entityKey}` : null;
  const [state, setState] = useState<{
    key: string | null;
    stamp: UpdatedAtStamp | null;
    justUpdated: boolean;
  }>({ key: null, stamp: null, justUpdated: false });
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!key || !tenantId || !entityKey) return;
    let cancelled = false;
    let ctl: ReturnType<typeof createLiveRefresh> | null = null;
    const fn = refetchRef.current;
    const memory = remembered(fn);
    const onVisibility = () => void ctl?.onVisibilityChange();
    const onFocus = () => void ctl?.onFocus();

    void fetchTabUpdatedAt(tenantId, entityKey).then((cached) => {
      if (cancelled) return;
      // Seen before: this report's data is from an earlier visit.
      const revisit = memory.has(key);
      const baseline = revisit ? (memory.get(key) ?? null) : cached;
      memory.set(key, baseline);
      if (!lastChecked.has(key)) noteChecked(key);
      setState({ key, stamp: baseline, justUpdated: false });
      ctl = createLiveRefresh({
        baseline,
        poll: () => readTabUpdatedAt(tenantId, entityKey).finally(() => noteChecked(key)),
        refetch: () => fn(tenantId),
        isVisible,
        onCommit: (stamp) => {
          memory.set(key, stamp);
          publishTabUpdatedAt(tenantId, entityKey, stamp);
          if (!cancelled) setState({ key, stamp, justUpdated: true });
        },
        onJustUpdated: (on) => {
          if (!cancelled) setState((s) => (s.key === key ? { ...s, justUpdated: on } : s));
        },
      });
      ctl.start();
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("focus", onFocus);
      // Back on a tab whose cached data predates a sync another view already
      // took: catch up now rather than at the next tick. Back after a while:
      // ask the sync record now, so old figures never sit for a poll interval.
      const stale = Date.now() - (lastChecked.get(key) ?? 0) >= REVISIT_POLL_AFTER_MS;
      if (stampAdvanced(baseline, cached) || (revisit && stale)) void ctl.pollNow();
    });
    return () => {
      cancelled = true;
      ctl?.stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [key, tenantId, entityKey]);

  if (!key || state.key !== key || !state.stamp) return null;
  const stamp = state.stamp;
  const label = formatUpdatedLabel(stamp.at, { dateOnly: stamp.dateOnly });
  return {
    ...stamp,
    label,
    text: updatedAtText(label),
    title: updatedAtTitle(stamp.at, stamp.entity, { dateOnly: stamp.dateOnly }),
    justUpdated: state.justUpdated,
  };
}
